interface EmbedErrorDetail {
  errorMsg?: string;
}

const UNKNOWN_EMBED_ERROR = "unknown error";

/**
 * Actions a dashboard's custom charts ask the page to perform, keyed by the
 * `eventType` the chart posts. The chart never opens windows itself: Luzmo
 * renders custom charts in a sandboxed iframe, and a tab opened from inside
 * it inherits the sandbox, which has no `allow-same-origin`. Our app then
 * cannot read localStorage or cookies and crashes on boot to a white page.
 * So the chart posts `{type: "customEvent", data: {eventType, ...}}` to its
 * parent, Luzmo relays it as a `customEvent` on the embed element, and this
 * page, which is not sandboxed, does the navigation.
 */
export const EmbeddedDashboardActionType = {
  OPEN_SHIPMENT: "openShipment",
  OPEN_EXTERNAL_LINK: "openExternalLink",
} as const;
export type EmbeddedDashboardActionType =
  (typeof EmbeddedDashboardActionType)[keyof typeof EmbeddedDashboardActionType];

export type EmbeddedDashboardAction =
  | {
      type: typeof EmbeddedDashboardActionType.OPEN_SHIPMENT;
      shipmentId: string;
    }
  | {
      type: typeof EmbeddedDashboardActionType.OPEN_EXTERNAL_LINK;
      url: string;
    };

interface CustomEventDetail {
  data?: {
    eventType?: unknown;
    shipmentId?: unknown;
    url?: unknown;
  } | null;
}

const SHIPMENT_ID_PATTERN = /^\d+$/;
const EXTERNAL_URL_PATTERN = /^https?:\/\//i;

function readDashboardActionFromPayload(
  payload: unknown,
): EmbeddedDashboardAction | null {
  const data = payload as CustomEventDetail["data"];
  if (data == null || typeof data !== "object") {
    return null;
  }
  if (data.eventType === EmbeddedDashboardActionType.OPEN_SHIPMENT) {
    const shipmentId = String(data.shipmentId ?? "");
    return SHIPMENT_ID_PATTERN.test(shipmentId)
      ? { type: EmbeddedDashboardActionType.OPEN_SHIPMENT, shipmentId }
      : null;
  }
  if (data.eventType === EmbeddedDashboardActionType.OPEN_EXTERNAL_LINK) {
    return typeof data.url === "string" && EXTERNAL_URL_PATTERN.test(data.url)
      ? { type: EmbeddedDashboardActionType.OPEN_EXTERNAL_LINK, url: data.url }
      : null;
  }
  return null;
}

/**
 * Parses the `customEvent` a custom chart posted into an action, or null when
 * the payload is not one this page knows how to perform. Field values are
 * validated rather than trusted: the chart code lives in Luzmo, and the only
 * things this page will open are a numeric shipment id on our own origin or
 * an http(s) URL.
 */
export function readDashboardAction(
  event: Event,
): EmbeddedDashboardAction | null {
  if (!(event instanceof CustomEvent)) {
    return null;
  }
  return readDashboardActionFromPayload(
    (event.detail as CustomEventDetail | null)?.data,
  );
}

const CUSTOM_EVENT_MESSAGE_TYPE = "customEvent";

interface CustomEventMessage {
  type?: unknown;
  data?: unknown;
}

/**
 * The same action, read from the `message` Luzmo re-posts on `window`.
 *
 * Observed 2026-09-09 with the webComponent embed: a custom chart's
 * `postMessage({type: "customEvent", data})` reaches this window twice — raw
 * from the sandboxed chart iframe (origin "null"), then re-posted by Luzmo's
 * dashboard code, which runs in this page — and the embed library never turns
 * it into the `customEvent` element event its documentation describes. Only
 * the re-posted copy is accepted: it comes from this window and this origin,
 * so the chart's own copy is skipped rather than handled twice.
 */
export function readDashboardActionFromMessage(
  event: MessageEvent,
): EmbeddedDashboardAction | null {
  if (event.source !== window || event.origin !== window.location.origin) {
    return null;
  }
  const message = event.data as CustomEventMessage | null;
  if (message?.type !== CUSTOM_EVENT_MESSAGE_TYPE) {
    return null;
  }
  return readDashboardActionFromPayload(message.data);
}

const MEASURE_DIMENSION_PICKER_UPDATE_ACTION = "measureDimensionPickerUpdate";

interface DashboardActionDetail {
  action?: unknown;
  item?: { id?: unknown } | null;
  measureDimensionPickerState?: unknown;
}

export interface MeasureDimensionPickerUpdate {
  /** The dashboard item (table) whose picker changed. */
  itemId: string;
  /** Luzmo's picker state for that item, kept opaque: it is stored and handed
   * back to Luzmo, never interpreted here. */
  pickerState: unknown;
}

/**
 * Reads a `dashboardAction` event into a picker update, or null for any other
 * action. Luzmo fires this element event in webComponent mode (verified on
 * dev 2026-09-16, unlike `customEvent`), with the shape
 * `{action: "measureDimensionPickerUpdate", item: {id}, measureDimensionPickerState: [...]}`.
 */
export function readMeasureDimensionPickerUpdate(
  event: Event,
): MeasureDimensionPickerUpdate | null {
  if (!(event instanceof CustomEvent)) {
    return null;
  }
  const detail = event.detail as DashboardActionDetail | null;
  if (detail?.action !== MEASURE_DIMENSION_PICKER_UPDATE_ACTION) {
    return null;
  }
  const itemId = detail.item?.id;
  if (
    typeof itemId !== "string" ||
    detail.measureDimensionPickerState == null
  ) {
    return null;
  }
  return { itemId, pickerState: detail.measureDimensionPickerState };
}

/**
 * The layout Luzmo chose for this viewer.
 *
 * A Luzmo dashboard is not one responsive design: it is a separate hand-built
 * canvas per screen mode, and `screenMode="auto"` picks one from the embed
 * container's width (desktop spans 1200-1599, largeScreen 1600 and up). Two
 * canvases of the same dashboard can therefore hold different items, so a tile
 * added to one is invisible to everyone the other is served to — which is
 * exactly how viewers on 1536px laptops spent a week on a stale Shipment
 * Tracking layout while wider colleagues saw the current one.
 *
 * Nothing we send Luzmo decides this, so the chosen mode is unknowable from
 * the backend and only observable here. Capturing it turns that failure into a
 * property breakdown instead of an investigation.
 */
export const EMBEDDED_DASHBOARD_SCREEN_MODES = [
  "mobile",
  "tablet",
  "desktop",
  "largeScreen",
  "fixed",
] as const;
export type EmbeddedDashboardScreenMode =
  (typeof EMBEDDED_DASHBOARD_SCREEN_MODES)[number];

interface ItemsRenderedDetail {
  data?: { screenMode?: unknown } | null;
}

/**
 * Reads the screen mode out of Luzmo's `itemsRendered` event, or null when the
 * payload does not carry a mode we know.
 *
 * The embed wraps its message as `{detail: {data: {type, dashboardId,
 * screenMode}}}` (see the itemsRendered branch in @luzmo/embed), the same
 * nesting `readDashboardAction` unwraps above. The value is checked against the
 * known modes rather than passed through, so a vendor change surfaces as a
 * missing property rather than as a new value quietly polluting the breakdown.
 */
export function readRenderedScreenMode(
  event: Event,
): EmbeddedDashboardScreenMode | null {
  if (!(event instanceof CustomEvent)) {
    return null;
  }
  const screenMode = (event.detail as ItemsRenderedDetail | null)?.data
    ?.screenMode;
  return EMBEDDED_DASHBOARD_SCREEN_MODES.includes(
    screenMode as EmbeddedDashboardScreenMode,
  )
    ? (screenMode as EmbeddedDashboardScreenMode)
    : null;
}

/**
 * Where the action navigates. Shipments resolve to a path on the current
 * origin, so the link lands in the environment the dashboard is embedded in
 * rather than the prod hostname the dataset's `shipment_url` column carries.
 */
export function getDashboardActionUrl(action: EmbeddedDashboardAction): string {
  if (action.type === EmbeddedDashboardActionType.OPEN_SHIPMENT) {
    return `/shipments/${action.shipmentId}`;
  }
  return action.url;
}

/**
 * Opens a URL in a new tab and reports whether the browser allowed it. The
 * open happens several async hops after the user's click (chart iframe →
 * postMessage → Luzmo → customEvent), so a popup blocker can refuse it.
 *
 * `noopener` is severed by hand instead of passed as a feature: with the
 * feature, window.open returns null unconditionally, which would make a
 * blocked popup indistinguishable from an opened one.
 */
export function openInNewTab(url: string): boolean {
  const openedWindow = window.open(url, "_blank");
  if (openedWindow === null) {
    return false;
  }
  openedWindow.opener = null;
  return true;
}

export function navigateInThisTab(url: string): void {
  window.location.assign(url);
}

/**
 * Luzmo's `error` event is a CustomEvent carrying `detail.errorMsg` (see
 * normalizeEmbedErrorDetail in @luzmo/embed), but the React binding types it as
 * a plain Event, so the detail has to be read defensively.
 */
export function readEmbedErrorMessage(event: Event): string {
  if (!(event instanceof CustomEvent)) {
    return UNKNOWN_EMBED_ERROR;
  }
  const detail = event.detail as EmbedErrorDetail | null;
  return detail?.errorMsg ?? UNKNOWN_EMBED_ERROR;
}
